// P2 stock (STK-C-04): reads from the stock views, writes through the stock operations (ADR-093, ADR-094).
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export type LocationKind = 'garage' | 'damaged';

export interface InventoryRow {
  product_id: string;
  name: string;
  sku: string | null;
  box_capacity: number | null;
  active: boolean;
  location_id: string;
  location: LocationKind | 'truck';
  boxes: number;
  loose: number;
  calc_pieces: number;
}
export interface MovementLine {
  product_id: string;
  product: string;
  box_capacity: number | null;
  location: LocationKind | 'truck';
  boxes: number;
  loose: number;
  reason: string | null;
  truck_id?: string | null;
  truck?: string | null;
}
export interface Movement {
  id: number;
  kind:
    | 'opening'
    | 'adjust'
    | 'count'
    | 'writeoff'
    | 'repack'
    | 'damaged_in'
    | 'receipt'
    | 'supplier_return'
    | 'load'
    | 'unload'
    | 'truck_count'
    | 'sale'
    | 'box_open';
  at: string;
  business_date: string;
  reason: string | null;
  actor_name: string | null;
  lines: MovementLine[];
}
export interface StockCheck {
  product_id: string;
  name: string;
  location: LocationKind | 'truck';
  stored_boxes: number;
  stored_loose: number;
  ledger_boxes: number;
  ledger_loose: number;
  matched: boolean;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}

export async function loadInventory(): Promise<InventoryRow[]> {
  return rows<InventoryRow>(await supabase.from('v_inventory').select('*').order('name'));
}
export async function loadMovements(limit = 12, product?: string): Promise<Movement[]> {
  const list = rows<Movement>(
    await supabase
      .from('v_movements')
      .select('*')
      .order('at', { ascending: false })
      .limit(product ? 200 : limit),
  );
  return product ? list.filter((m) => m.lines.some((l) => l.product_id === product)) : list;
}
export async function loadStockCheck(): Promise<StockCheck[]> {
  return rows<StockCheck>(await supabase.from('v_stock_check').select('*').order('name'));
}
export async function loadOpeningOpen(): Promise<boolean> {
  const list = rows<{ opening_open: boolean }>(await supabase.from('v_stock_status').select('opening_open'));
  return list[0]?.opening_open ?? false;
}

export interface StockLine {
  product: string;
  boxes: number;
  loose: number;
  reason?: string | undefined;
  cost?: number | undefined;
}
export const postOpeningStock = (lines: StockLine[]) => sendCommand('post_opening_stock', { p_lines: lines });
export const adjustGarage = (
  product: string,
  direction: 'increase' | 'decrease',
  boxes: number,
  loose: number,
  reason: string,
) =>
  sendCommand('adjust_garage', {
    p_product: product,
    p_direction: direction,
    p_boxes: boxes,
    p_loose: loose,
    p_reason: reason,
  });
export const countGarage = (lines: StockLine[]) =>
  sendCommand<{ differences: number }>('count_garage', { p_lines: lines });
export const writeOffDamaged = (lines: StockLine[]) => sendCommand('write_off_damaged', { p_lines: lines });
export const repackGarage = (product: string, boxes: number) =>
  sendCommand('repack_garage', { p_product: product, p_boxes: boxes });
